import { createFileRoute } from '@tanstack/react-router'
import {
  Building2,
  Compass,
  FileText,
  LayoutTemplate,
  Library,
  LineChart,
  ListChecks,
  Radio,
  Settings,
  ShieldCheck,
  Sparkles,
  Zap,
  type LucideIcon,
} from 'lucide-react'
import { DocsContentsRail, DocsContentsRow } from '@/components/DocsContents'
import { PageHeader } from '@/components/PageHeader'
import { useActiveSection } from '@/lib/use-active-section'

/**
 * The guide — the app's manual for the Clipbook team that runs it, as a docs page: a sticky table of
 * contents on the left (with scroll-spy highlighting the chapter you're reading) and the chapters on
 * the right. Reached from the Guide link at the foot of the sidebar. Written from the OPERATOR's seat
 * — you work across every account, drill into a customer's, and configure the products they receive —
 * not the customer's; a customer-facing guide is a later concern. Content lives here as data
 * (chapters → topics, some with bullet points) so it stays a scannable reference. Keep it current:
 * when a feature lands or changes, its topic here changes with it.
 */
export const Route = createFileRoute('/app/guide')({
  component: GuidePage,
})

type GuideTopic = { title: string; body: string; points?: string[] }
type GuideChapter = {
  id: string
  icon: LucideIcon
  title: string
  /** One-sentence framing shown under the chapter title. */
  lead: string
  topics: GuideTopic[]
}

const CHAPTERS: GuideChapter[] = [
  {
    id: 'getting-around',
    icon: Compass,
    title: 'Getting around',
    lead: 'You work on two planes — the Clipbook level above every account, and the account you drill into — plus shortcuts that make moving fast.',
    topics: [
      {
        title: 'The two planes',
        body: "The ROOT (Clipbook) plane sits above every account: the Accounts list, the cross-account tables, and the shared libraries. Drill into an account and the sidebar becomes that account's — its products and resources. A back row climbs you back out; the brand badge by the logo says which plane you're on.",
      },
      {
        title: 'Quick search',
        body: 'Press ⌘K (Ctrl-K on Windows) or click Quick search to jump to any account or page by name — a dropdown that filters as you type, ↑/↓ to move, Enter to go. The fastest way across hundreds of accounts.',
      },
      {
        title: 'The workspace switcher',
        body: "Inside an account, the switcher under the search box moves you between it and its clients, and keeps the surface you're on — one account's Reports to another's Reports — rather than dropping to Home.",
      },
      {
        title: 'Session tabs',
        body: 'The thin strip along the top works like browser tabs: hold several accounts open at once, close what you are done with. They reset on reload — the URL is always the source of truth.',
      },
      {
        title: 'The small stuff',
        body: 'The breadcrumb in the header is the trail back. Collapse the sidebar to an icon rail with the chevron or the [ key (remembered); the theme toggle switches light and dark, otherwise following your system.',
      },
    ],
  },
  {
    id: 'accounts',
    icon: Building2,
    title: 'Accounts & the tenant model',
    lead: 'Every customer is an account; the Accounts list is where you browse, open and create them.',
    topics: [
      {
        title: 'The Accounts list',
        body: "The root plane's Accounts page browses every customer, each row showing its sub-account, topic and product counts and its status. Create a new account there (it comes with its first sub-account), or click a row to drill in.",
      },
      {
        title: 'Accounts and clients',
        body: "An account owns client sub-accounts — an agency's brands, or a company's teams that keep separate data. Exactly one sub-account is the account's own primary space; the rest are clients. Topics, reports and the coverage stream all belong to a sub-account, not the account directly.",
      },
      {
        title: "The customer's Home",
        body: "Drilling into an account lands on the Home the customer sees: a welcome in their brand, their recent reports, and the product shelf. A client's Home is the same, scoped to that client.",
      },
      {
        title: 'Sub-accounts & Settings',
        body: "The account's Settings is where you set its structure (sub-accounts), its plan (products and data sources), and its seat cap — and where a customer admin manages their own people within that allowance.",
      },
    ],
  },
  {
    id: 'plans',
    icon: Sparkles,
    title: 'Plans & entitlements',
    lead: 'What a customer can use is their entitlements — the products and data sources you provision.',
    topics: [
      {
        title: 'Products',
        body: 'Reports, Library, Analytics and real-time alerts are the delivery products. A product an account is not entitled to simply is not on its shelf; the account page shows the true plan to everyone, so a toggle is immediately visible the way the customer will see it.',
      },
      {
        title: 'Data sources',
        body: 'Topics pull from the sources the account is entitled to — News, LexisNexis full text, X, LinkedIn, Broadcast, Podcast and Newsletters. A source outside the plan shows locked in the topic editor rather than silently missing.',
      },
      {
        title: 'Setting the plan',
        body: "Products and data sources are entitlement rows toggled in the account's Settings → Products. Staff bypass entitlement gates at the API (so you can set an account up before a deal closes), but the UI gates everyone — including staff — so what you toggle is what they get.",
      },
    ],
  },
  {
    id: 'topics',
    icon: Zap,
    title: 'Topics',
    lead: 'A topic defines what coverage a workspace collects — the shared building block behind reports, the Library and alerts.',
    topics: [
      {
        title: 'The topic wizard',
        body: "In a client's workspace open Topics and choose New topic. The wizard walks four steps:",
        points: [
          'Details — name, description, and tags.',
          'Sources — turn on News and the platform sources the account is entitled to, with a boolean query for each.',
          'Reasoning filter — an optional AI relevance pass: ADVISE flags off-topic items, FILTER drops them.',
          'Review — go live now, or save as a draft.',
        ],
      },
      {
        title: 'Queries and tags',
        body: 'Queries are boolean expressions — Acme AND (CEO OR founder) — with per-source options (X can restrict to verified authors; podcasts can prioritise creators). Tags are a shared pool per workspace, each a named entity (ORG, PERSON, LOCATION, …) colour-coded by entity everywhere it shows, including the Library. Apply them to a topic or to individual queries.',
      },
      {
        title: 'Coverage',
        body: 'Coverage rules — outlet lists, countries, languages — narrow what a topic keeps, so it reads the whole web but reports only what passes. The same rules bound how far the Library and alerts read, which is what keeps a busy topic from pulling hundreds of stories for every one worth alerting on.',
      },
      {
        title: 'Reuse and lifecycle',
        body: 'A LIVE topic is collecting; a DRAFT is paused. Two identical live topics are blocked rather than silently double-collecting.',
        points: [
          'Any topic can feed any product — reports, the Library, or alerts; "Used for" records which.',
          "Copy a topic to the account's other workspaces; each copy is independent, tags included.",
          'Clone a topic only when its content must genuinely differ — otherwise attach the one topic to several products.',
          'The Topics Library (root plane) is every topic across every client, with cross-account copy.',
        ],
      },
    ],
  },
  {
    id: 'templates',
    icon: LayoutTemplate,
    title: 'Templates',
    lead: "A template is a layout; the colour and typeface come from the account, so one layout renders in the customer's brand.",
    topics: [
      {
        title: 'Account templates and the shared library',
        body: 'An account has its own templates; above them, the Template Library (root plane) is the shared pool you curate — assign an owner, or copy a layout to many accounts at once. A template is an ordered set of blocks (masthead, intro, sections, footer); brand lives on the account, not the template.',
      },
      {
        title: 'Where they apply',
        body: "A report picks its template on its Template tab. Clients inherit the account's templates — the same set appears in every client workspace.",
      },
    ],
  },
  {
    id: 'coverage-lists',
    icon: ListChecks,
    title: 'Coverage lists',
    lead: 'Named, reusable sets — outlets, countries, languages, priorities — that reports reference LIVE.',
    topics: [
      {
        title: 'Global and provisioned',
        body: "The Coverage List Library (root plane) holds the global built-ins you maintain plus lists provisioned to a single account. Accounts consume them in a report's coverage; they don't manage the pool.",
      },
      {
        title: 'Live references',
        body: 'Reports reference a list rather than copying it, so editing one updates every report and header that uses it — worth remembering before editing a widely-used list.',
      },
    ],
  },
  {
    id: 'reports',
    icon: FileText,
    title: 'Reports',
    lead: 'A report is two things — a standing CONFIGURATION of what goes out, and the ISSUES generated from it.',
    topics: [
      {
        title: 'Configure',
        body: "Click a report's NAME to open its configuration. Everything auto-saves — there is no Save button. The tabs:",
        points: [
          'Content — which topics feed it, and the coverage filters that narrow them.',
          'Headers — the sections it is organised into (a report needs at least one before its first draft).',
          'Template — the layout it renders in.',
          'Delivery — the schedule, recipients, and subject line.',
          'Derek — the AI prompts that help draft and summarise.',
        ],
      },
      {
        title: 'Generate',
        body: 'The Edit column is the daily workflow: New draft creates the issue and opens the editor, where you curate items, edit copy and send; Previous reports is the archive plus any draft in progress. The All reports table on the root plane is the same split across every account.',
      },
      {
        title: 'Automatic sends',
        body: "A report on an automatic schedule writes its own issue ahead of each send moment, builds it from the same pull the editor uses, and sends — a late build still goes out rather than being skipped. The subject can carry date tokens like {{MMM D, YYYY}}, filled at send time in the report's timezone.",
      },
      {
        title: 'Delivery, legacy and pauses',
        body: 'A migrated report stays on legacy delivery until the Delivery tab\'s "Delivered by" switch moves it to Headline; until then Headline shows its coverage but won\'t send. The delivery kill switch pauses the whole fleet — held sends wait and go out when it lifts, and everyone sees the banner.',
      },
    ],
  },
  {
    id: 'library',
    icon: Library,
    title: 'Library',
    lead: "The Library is a workspace's coverage stream, article by article — everything it matched, filterable.",
    topics: [
      {
        title: 'The stream and the window',
        body: "The Library reads the sub-account's cached coverage stream (it computes nothing itself). Pick the window — 30, 90 or 365 days, or a custom range — and it loads the newest coverage in it, up to a cap for the busiest workspaces; the page says how far back it reached.",
      },
      {
        title: 'Filtering and the numbers',
        body: "Facets narrow within the loaded window — media type, outlet, topic, tag, sentiment, classification — filtering instantly, client-side. The KPIs up top summarise the window, each with a change against the equal period before it (blank only when the data doesn't reach back far enough).",
      },
      {
        title: 'A single article',
        body: 'Click a row for the article panel: the full record, its passages, sentiment and classification, entity-coloured tags, engagement, and — for broadcast and podcasts — the clip. A broadcast clip listed twice in the feed is folded into one row, the station as its outlet. Save a set of filters as a Library view to return to it.',
      },
    ],
  },
  {
    id: 'analytics',
    icon: LineChart,
    title: 'Analytics',
    lead: 'Analytics is the aggregate view of the same stream the Library shows — the shape of coverage, not the rows.',
    topics: [
      {
        title: 'Two views of one stream',
        body: 'Library and Analytics read the same stream: the Library is row-level, Analytics is the roll-up — volume over time, by outlet, topic and sentiment. The window and coverage apply to both.',
      },
      {
        title: 'Dashboards and groups',
        body: 'Build charts into dashboards and group facets to compare segments. Dashboards and facet groups live in the browser for now, so they are per-device until saved server-side lands.',
      },
    ],
  },
  {
    id: 'rtas',
    icon: Radio,
    title: 'Real-time alerts',
    lead: "An RTA watches a workspace's topics and fires the moment qualifying coverage breaks — to Slack, email, or both.",
    topics: [
      {
        title: 'What an RTA listens to',
        body: 'An RTA belongs to a sub-account and listens on the same topics everything else uses — no separate query. Coverage rules decide which outlets count, so it fires on the stories that matter rather than everything a topic touches.',
      },
      {
        title: 'Delivery and the feed',
        body: "Alerts go to a Slack channel, email, or both, each sent once per channel — no duplicates when a check overlaps. The RTA's own feed lists what it has fired, newest first. A migrated RTA keeps its legacy delivery until it is switched over, the way reports do.",
      },
      {
        title: 'Across accounts',
        body: 'The RTAs table on the root plane is every alert across every account, most-pending first — the landing for working alerts without drilling in and out.',
      },
    ],
  },
  {
    id: 'access',
    icon: ShieldCheck,
    title: 'Access control',
    lead: 'Two layers: capabilities granted on an account, and the org-wide platform plane staff manage.',
    topics: [
      {
        title: "An account's own access",
        body: 'On an account\'s access section you grant people plain capabilities — view, edit, manage access — per workspace, across the whole account tree in one invitation. Seats cap how many people an account may hold. Someone with "manage access" can delegate up to, never beyond, what they hold themselves.',
      },
      {
        title: 'Time-boxed grants',
        body: 'A grant can carry an expiry — "edit until Friday" — and revokes itself on the deadline. Granting something a person already holds permanently is refused rather than silently downgraded.',
      },
      {
        title: 'The platform plane',
        body: "Access Control on the root plane manages the org-wide layer: who is staff, the roles and permission slugs they carry, and every member across all accounts (Users and Roles tabs). Customer-facing access stays on each account's own section.",
      },
    ],
  },
  {
    id: 'operations',
    icon: Settings,
    title: 'Operations & delivery',
    lead: 'The controls behind sending — pauses, the shared notice, and the safety rails that keep delivery honest.',
    topics: [
      {
        title: 'The global notice',
        body: 'Clipbook-wide Settings holds the global notice: while it is set (and inside its optional date window) it rides as the intro of every report that goes out. Edit it in one place; it vanishes from every report the day its window ends.',
      },
      {
        title: 'The kill switches',
        body: 'Two switches pause the fleet: generation (the automatic tick) and delivery (every send). A pause is a decision, not a failure — held sends wait and go out when it lifts, nothing is counted as failed, and everyone sees the banner.',
      },
      {
        title: 'Send safety rails',
        body: 'A few guarantees worth knowing when something looks stuck:',
        points: [
          'Live email and Slack sending are per-environment flags; where off, sends are stubbed and marked so, never silently dropped.',
          'A dry-run preview shows exactly what a report would send right now, writing nothing.',
          'One report per environment is the automatic-send canary the daily health check reads.',
          'A send that cannot go out is marked failed with the reason, never left hanging.',
        ],
      },
    ],
  },
]

const CHAPTER_IDS = CHAPTERS.map((chapter) => chapter.id)

function GuidePage() {
  const activeId = useActiveSection(CHAPTER_IDS)

  return (
    <div className="mx-auto w-full max-w-5xl">
      <PageHeader
        title="Guide"
        description="How Clipbook works, for the team that runs it — the two planes, the accounts you operate, and the daily workflows inside them. Use the contents to jump, or read it through."
      />

      <DocsContentsRow items={CHAPTERS} label="Guide contents" />

      <div className="lg:grid lg:grid-cols-[13rem_1fr] lg:gap-10">
        <DocsContentsRail items={CHAPTERS} activeId={activeId} label="Guide contents" />
        <div className="flex min-w-0 flex-col gap-10">
          {CHAPTERS.map((chapter) => (
            <ChapterSection key={chapter.id} chapter={chapter} />
          ))}
          <p className="border-t border-border pt-6 text-sm text-muted-foreground">
            Something out of date, or a workflow that doesn&rsquo;t match what you see? Fix the
            topic here — this guide is meant to track the app.
          </p>
        </div>
      </div>
    </div>
  )
}

function ChapterSection({ chapter }: { chapter: GuideChapter }) {
  const Icon = chapter.icon
  return (
    // scroll-mt clears the sticky header when a contents link jumps here.
    <section id={chapter.id} className="scroll-mt-24">
      <div className="flex items-start gap-3">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-accent/60 text-primary">
          <Icon className="size-4.5" />
        </div>
        <div className="min-w-0">
          <h2 className="text-lg font-semibold tracking-tight">{chapter.title}</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">{chapter.lead}</p>
        </div>
      </div>

      <dl className="mt-4 flex flex-col gap-3">
        {chapter.topics.map((topic) => (
          <div key={topic.title} className="rounded-lg border border-border p-4">
            <dt className="text-sm font-semibold">{topic.title}</dt>
            <dd className="mt-1 text-sm leading-relaxed text-muted-foreground">
              {topic.body}
              {topic.points ? (
                <ul className="mt-2 flex list-disc flex-col gap-1 pl-5 marker:text-muted-foreground/50">
                  {topic.points.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>
              ) : null}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  )
}
